import { useState, useEffect } from 'react';
import API_BASE_URL from '../Config/config';

export const useSubscription = () => {
    const [subscription, setSubscription] = useState({
        isLoaded: false,
        isPaid: false,
        plan: 'free',
        daysRemaining: null,
        hasAiAccess: false,
        limits: {
            mcqs: 5,
            docs: 2,
            mockInterviews: 1
        },
        elite: {
            selection: null,
            isLoaded: false
        },
        usage: {
            mockInterviews: 0,
            isLoaded: false
        }
    });

    const fetchSubscriptionData = async () => {
        // Tenant students should have unrestricted access handled by college admin.
        // Detect tenant subdomains (anything.other than www on *.theskillgenie.com)
        try {
            const host = typeof window !== 'undefined' ? (window.location?.hostname || '') : '';
            const isTenantHost = host.endsWith('.theskillgenie.com') && !host.startsWith('www.');
            
            // Check if user is a college student from localStorage
            const storedUser = localStorage.getItem("user");
            if (!storedUser) {
                 // reset state if no user
                 setSubscription({
                    isLoaded: true,
                    isPaid: false,
                    plan: 'free',
                    daysRemaining: null,
                    hasAiAccess: false,
                    limits: { mcqs: 5, docs: 2, mockInterviews: 1 },
                    elite: { selection: null, isLoaded: true },
                    usage: { mockInterviews: 0, isLoaded: true }
                });
                return;
            }

            let isCollegeUser = false;
            try {
                if (storedUser) {
                    const parsedUser = JSON.parse(storedUser);
                    isCollegeUser = parsedUser?.role === 'student' && (parsedUser?.tenant || parsedUser?.college_id || parsedUser?.institution_id);
                }
            } catch (e) {}

            if (isTenantHost || isCollegeUser) {
                const getUserIdFromStorage = () => {
                    try {
                        const user = JSON.parse(localStorage.getItem("user") || "{}");
                        return user?.userId || user?.user_id || localStorage.getItem("user_id") || null;
                    } catch { return null; }
                };
                const userId = getUserIdFromStorage();
                
                // Fetch Elite Selection for college users too
                let eliteSelection = null;
                if (userId) {
                    try {
                        const sessionToken = localStorage.getItem('session_token');
                        const eliteRes = await fetch(`${API_BASE_URL}/api/elite/selection`, {
                            headers: { 
                                'user-id': userId,
                                ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
                            }
                        });
                        if (eliteRes.ok) {
                            const eliteData = await eliteRes.json();
                            eliteSelection = eliteData.selection;
                        }
                    } catch (e) {
                        console.warn("Elite selection fetch failed for college user:", e.message);
                    }
                }

                setSubscription({
                    isLoaded: true,
                    isPaid: true,
                    plan: 'college',
                    hasAiAccess: true, // Tenant gets full access in college portal
                    limits: {
                        mcqs: Infinity,
                        docs: Infinity,
                        mockInterviews: Infinity
                    },
                    elite: {
                        selection: eliteSelection,
                        isLoaded: true
                    },
                    usage: {
                        mockInterviews: 0,
                        mockInterviewsByType: {},
                        isLoaded: true
                    }
                });
                return; // Skip learner-only subscription calls which also avoids CORS issues
            }
        } catch (_e) {
            // ignore host parsing issues and continue with learner flow
        }
        const storedUser = localStorage.getItem("user");
        let userId = null;
        try {
            const parsedUser = storedUser ? JSON.parse(storedUser) : null;
            userId = parsedUser?.userId || parsedUser?.id || parsedUser?.user_id || localStorage.getItem("user_id");
        } catch (err) {
            userId = localStorage.getItem("user_id");
        }

        if (!userId) {
            setSubscription(prev => ({ ...prev, isLoaded: true }));
            return;
        }

        try {
            // 1. Fetch Subscription Status (Learner flow only)
            const encodedUserId = encodeURIComponent(userId);
            const subRes = await fetch(`${API_BASE_URL}/api/skillgenie-premium/status/${encodedUserId}`, {
                headers: { 'user-id': userId }
            });
            const subData = await subRes.json();

            console.log("🔍 Subscription API Response:", subData);

            let isPaid = false;
            let hasAiAccess = false;
            let plan = 'free';
            let daysRemaining = null;

            // Check if user has active subscription
            if (subData.hasActiveSubscription && subData.subscription) {
                isPaid = true;
                const amount = Number(subData.subscription.amount);
                // 3 month (7999) or 6 month (14999) plans get AI, OR if it's a college plan
                hasAiAccess = (amount >= 7999 || subData.subscription.plan_name === 'college');
                plan = subData.subscription.plan_name;
                daysRemaining = subData.subscription.daysRemaining;
                
                // If daysRemaining is missing from API, calculate it manually from end_date
                if (daysRemaining === null && subData.subscription.end_date) {
                    const now = new Date();
                    const end = new Date(subData.subscription.end_date);
                    if (end > now) {
                        daysRemaining = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
                    }
                }
                
                console.log("✅ PAID OR COLLEGE USER DETECTED:", { isPaid, hasAiAccess, plan, amount, daysRemaining });
            } else {
                console.log("❌ FREE USER:", { hasActiveSubscription: subData.hasActiveSubscription, subscription: subData.subscription });
            }

            // 2. Fetch Elite Selection (with error handling for CORS/403)
            let eliteData = { selection: null };
            try {
                const eliteRes = await fetch(`${API_BASE_URL}/api/elite/selection`, {
                    headers: { 'user-id': userId }
                });
                if (eliteRes.ok) {
                    eliteData = await eliteRes.json();
                }
            } catch (e) {
                console.warn("Elite selection fetch failed (CORS/403 expected):", e.message);
            }

            // 3. Fetch Mock Interview Usage (with error handling for CORS/403)
            let usageData = { count: 0 };
            try {
                const usageRes = await fetch(`${API_BASE_URL}/api/usage/mock-interview`, {
                    headers: { 'user-id': userId }
                });
                if (usageRes.ok) {
                    usageData = await usageRes.json();
                } else {
                    // Fallback to local storage tracker if API fails (502/CORS)
                    try {
                        const localCount = Number(localStorage.getItem('mock_interview_usage_count') || 0);
                        usageData = { count: localCount };
                    } catch (e) {}
                }
            } catch (e) {
                console.warn("Usage data fetch failed (CORS/403 expected):", e.message);
                // Fallback to local storage tracker if API fails (502/CORS)
                try {
                    const localCount = Number(localStorage.getItem('mock_interview_usage_count') || 0);
                    usageData = { count: localCount };
                } catch (e) {}
            }

            setSubscription({
                isLoaded: true,
                isPaid,
                plan,
                daysRemaining,
                hasAiAccess,
                limits: {
                    mcqs: isPaid ? Infinity : 5,
                    docs: isPaid ? Infinity : 2,
                    mockInterviews: isPaid ? Infinity : 1
                },
                elite: {
                    selection: eliteData.selection?.selected_technology || null,
                    isLoaded: true
                },
                usage: {
                    mockInterviews: usageData.count || 0,
                    mockInterviewsByType: usageData.usageByType || {},
                    isLoaded: true
                }
            });
        } catch (error) {
            console.error('Error fetching subscription data:', error);
            setSubscription(prev => ({ ...prev, isLoaded: true }));
        }
    };

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    // Helper function to check feature access
    const canAccessFeature = (feature, options = {}) => {
        // College/tenant users have full access
        const host = typeof window !== 'undefined' ? (window.location?.hostname || '') : '';
        const isTenantStudent = (host.endsWith('.theskillgenie.com') && !host.startsWith('www.')) || 
                                (host !== 'localhost' && !host.includes('theskillgenie.com'));

        // Check if user is a college student from localStorage
        const storedUser = localStorage.getItem("user");
        let isCollegeUser = false;
        try {
            if (storedUser) {
                const parsedUser = JSON.parse(storedUser);
                isCollegeUser = parsedUser?.role === 'student' && (parsedUser?.tenant || parsedUser?.college_id || parsedUser?.institution_id);
            }
        } catch (e) {}

        // All paid users (1/3/6 months) and tenant users have full access including TSG
        if (isTenantStudent || isCollegeUser || subscription.isPaid) return true;

        // Free user restrictions
        switch (feature) {
            case 'TECH_DOCUMENTS':
            case 'ELITE_DOCS':
                // 2 documents per technology
                return (options.docIndex !== undefined && options.docIndex < 2);

            case 'TECH_EXPLORER_MCQ':
            case 'LAUNCHPAD_MCQ':
            case 'MCQ_LEVEL':
                // Levels 1-5 only
                return (options.level !== undefined && options.level <= 5);

            case 'MOCK_INTERVIEW':
                // For learners: Initially only one feature (Technical) is accessible.
                // To unlock all 5, user has to take subscription.
                const usageCount = subscription.usage?.mockInterviews || 0;
                
                // If it's the free feature (Technical), allow one trial
                if (options.type === 'technical') {
                    return usageCount < 1;
                }
                
                // All other types (HR, Coding, etc.) are locked for free users
                return false;

            case 'TSG_NOTES':
            case 'TSG_AI':
                return false; // No access for free users

            case 'BOOTCAMP':
                // 1 per feature type
                const featureType = options.featureType || 'general';
                const featureUsed = subscription.usage?.bootcamp?.[featureType] || 0;
                return featureUsed < 1;

            case 'MCQ_LEVEL':
                // Levels 1-5 only
                return (options.level !== undefined && options.level <= 5);

            default:
                return false;
        }
    };

    return { ...subscription, refresh: fetchSubscriptionData, canAccessFeature };
};

export default useSubscription;
